import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import type { GeoJSONFeatureCollection, MapView, StyleId } from '../types/gis';
import { applyViewHint } from '../utils/applyViewHint';
import { captureMapCanvas } from '../utils/exportPng';
import { buildFeaturePopupNode } from '../utils/popupRenderer';

const CLUSTER_THRESHOLD_DEFAULT = 10000;

export interface UseMapEngineOptions {
  geojson: GeoJSONFeatureCollection;
  view?: MapView | null;
  styleId?: StyleId;
  styleSpec?: any;                  // MapLibre style JSON; if omitted, uses sensible default
  interactive?: boolean;            // default true; set false for inline previews
  preserveDrawingBuffer?: boolean;  // default false; true for the full viewer
  clusterThreshold?: number;        // default 10000; tune per surface
  lazyMount?: boolean;              // default false; true for inline previews -> IntersectionObserver
  /** Layer names (matched against features' `properties.layer`, defaulting
   *  to 'default' when absent) that should be hidden from the canvas.
   *  Empty / undefined = render everything. Driven by LayerTogglePanel. */
  hiddenLayers?: ReadonlySet<string>;
}

/** Build a MapLibre filter expression that scopes a layer to one geometry
 *  type AND (optionally) excludes features whose `properties.layer` is in
 *  the hidden set. Untagged features fall through to the 'default' bucket. */
function buildLayerFilter(
  geomType: 'Point' | 'LineString' | 'Polygon',
  hiddenLayers: ReadonlySet<string> | undefined,
): any {
  const geomFilter: any = ['==', ['geometry-type'], geomType];
  if (!hiddenLayers || hiddenLayers.size === 0) return geomFilter;
  const hiddenList = Array.from(hiddenLayers);
  const layerExpr: any = ['coalesce', ['get', 'layer'], 'default'];
  return ['all', geomFilter, ['!', ['in', layerExpr, ['literal', hiddenList]]]];
}

export interface UseMapEngineHandle {
  containerRef: React.RefObject<HTMLDivElement>;
  mapInstance: maplibregl.Map | null;
  isReady: boolean;
  fitBounds: () => void;
  exportPng: () => Promise<Blob>;
  exportThumbnail: (size?: { w: number; h: number }) => Promise<string>;
  setStyle: (id: StyleId) => void;
}

export function useMapEngine(opts: UseMapEngineOptions): UseMapEngineHandle {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const [shouldMount, setShouldMount] = useState(!opts.lazyMount);

  // IntersectionObserver lazy mount for inline previews
  useEffect(() => {
    if (!opts.lazyMount || shouldMount) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldMount(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [opts.lazyMount, shouldMount]);

  // Build MapLibre instance
  useEffect(() => {
    if (!shouldMount) return;
    const container = containerRef.current;
    if (!container) return;

    const styleSpec = opts.styleSpec ?? {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors',
        },
      },
      layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm' }],
    };

    const initialView = applyViewHint(opts.view ?? null) ?? {
      center: [0, 0] as [number, number],
      zoom: 1,
      bearing: 0,
    };

    const map = new maplibregl.Map({
      container,
      style: styleSpec,
      center: initialView.center,
      zoom: initialView.zoom,
      bearing: initialView.bearing,
      interactive: opts.interactive !== false,
      preserveDrawingBuffer: opts.preserveDrawingBuffer === true,
    });

    mapRef.current = map;

    const handleLoad = () => {
      const fc = opts.geojson;
      const featureCount = fc.features?.length ?? 0;
      const useClusters = featureCount > (opts.clusterThreshold ?? CLUSTER_THRESHOLD_DEFAULT);

      try {
        map.addSource('artifact', {
          type: 'geojson',
          data: fc as any,
          cluster: useClusters,
          clusterRadius: 40,
        });

        map.addLayer({
          id: 'artifact-circles',
          type: 'circle',
          source: 'artifact',
          paint: {
            'circle-radius': useClusters ? 6 : 5,
            'circle-color': ['coalesce', ['get', 'color'], '#0066cc'],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
          },
          filter: buildLayerFilter('Point', opts.hiddenLayers),
        });

        map.addLayer({
          id: 'artifact-lines',
          type: 'line',
          source: 'artifact',
          paint: {
            'line-color': ['coalesce', ['get', 'color'], '#0066cc'],
            'line-width': 2,
          },
          filter: buildLayerFilter('LineString', opts.hiddenLayers),
        });

        map.addLayer({
          id: 'artifact-fills',
          type: 'fill',
          source: 'artifact',
          paint: {
            'fill-color': ['coalesce', ['get', 'color'], '#0066cc'],
            'fill-opacity': 0.35,
          },
          filter: buildLayerFilter('Polygon', opts.hiddenLayers),
        });
      } catch (e) {
        console.warn('useMapEngine layer-add failed', e);
      }

      // Click → text-only feature-properties popup (XSS-safe via
      // popupRenderer.buildFeaturePopupNode). Cursor flips to pointer on
      // hover so the affordance is visible. Registered for all three
      // base layers (circles/lines/fills) so any geometry type pops.
      const POPUP_LAYERS = ['artifact-circles', 'artifact-lines', 'artifact-fills'];
      for (const layerId of POPUP_LAYERS) {
        try {
          map.on('click', layerId, (e: any) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const node = buildFeaturePopupNode(feature.properties as any);
            // For Point clicks, anchor the popup at the feature's coords;
            // for Lines/Polygons use the click location.
            const coords = feature.geometry?.type === 'Point'
              ? (feature.geometry.coordinates as [number, number])
              : [e.lngLat.lng, e.lngLat.lat] as [number, number];
            new maplibregl.Popup({ closeButton: true, closeOnClick: true })
              .setLngLat(coords)
              .setDOMContent(node)
              .addTo(map);
          });
          map.on('mouseenter', layerId, () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', layerId, () => {
            map.getCanvas().style.cursor = '';
          });
        } catch (popupErr) {
          console.warn(`useMapEngine popup wire-up failed for ${layerId}`, popupErr);
        }
      }

      // Auto-fit to actual data on load. The view_hint baked into
      // construction is used for the INITIAL center/zoom (so the map
      // doesn't flash from world view to data view), but a precise
      // fitBounds against real feature coordinates handles edge cases
      // where compute_view_hint's heuristic clips features at the
      // viewport edge — the inline-preview aspect ratio is wider than
      // the heuristic assumes, so a few features end up just off-screen.
      try {
        // Normalize Feature OR FeatureCollection -> features list.
        // Convex-hull / single-shape ops return a Feature; multi-feature
        // collections return an FC. Both pathways need auto-fit.
        const featuresList: any[] = (fc as any).type === 'Feature'
          ? [fc]
          : ((fc as any).features || []);
        const positions: Array<[number, number]> = [];
        for (const feat of featuresList) {
          const geom: any = feat.geometry;
          if (!geom || !geom.coordinates) continue;
          if (geom.type === 'Point') {
            positions.push(geom.coordinates as [number, number]);
          } else if (geom.type === 'LineString' || geom.type === 'MultiPoint') {
            for (const c of geom.coordinates) positions.push(c);
          } else if (geom.type === 'Polygon') {
            for (const ring of geom.coordinates) for (const c of ring) positions.push(c);
          } else if (geom.type === 'MultiPolygon' || geom.type === 'MultiLineString') {
            for (const a of geom.coordinates) for (const b of a) {
              if (Array.isArray(b[0])) for (const c of b) positions.push(c);
              else positions.push(b);
            }
          }
        }
        if (positions.length >= 1) {
          let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
          for (const [lng, lat] of positions) {
            if (lng < minLng) minLng = lng;
            if (lat < minLat) minLat = lat;
            if (lng > maxLng) maxLng = lng;
            if (lat > maxLat) maxLat = lat;
          }
          if (positions.length === 1) {
            map.flyTo({ center: positions[0], zoom: 12, duration: 0 });
          } else {
            map.fitBounds(
              [[minLng, minLat], [maxLng, maxLat]],
              { padding: 40, duration: 0, maxZoom: 14 },
            );
          }
        }
      } catch (e) {
        console.warn('useMapEngine fit-to-data failed (using view_hint fallback)', e);
      }

      setMapInstance(map);
      setIsReady(true);
    };

    map.on('load', handleLoad);

    return () => {
      setIsReady(false);
      setMapInstance(null);
      map.remove();
      mapRef.current = null;
    };
  }, [shouldMount, opts.styleSpec, opts.preserveDrawingBuffer, opts.interactive, opts.clusterThreshold]);

  // Update geojson source when fc reference changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isReady) return;
    const source = map.getSource('artifact') as any;
    if (source && typeof source.setData === 'function') {
      source.setData(opts.geojson as any);
    }
  }, [opts.geojson, isReady]);

  // Re-apply the layer filter when the hidden-layers set changes. Parent
  // components must pass a NEW Set reference on each toggle for this to
  // run (the LayerTogglePanel does that via setHiddenLayers(prev => new
  // Set(prev))).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isReady) return;
    try {
      map.setFilter('artifact-circles', buildLayerFilter('Point', opts.hiddenLayers));
      map.setFilter('artifact-lines', buildLayerFilter('LineString', opts.hiddenLayers));
      map.setFilter('artifact-fills', buildLayerFilter('Polygon', opts.hiddenLayers));
    } catch (e) {
      console.warn('useMapEngine setFilter failed', e);
    }
  }, [opts.hiddenLayers, isReady]);

  // Style hot-swap
  const setStyle = useCallback((_id: StyleId) => {
    const map = mapRef.current;
    if (!map || !opts.styleSpec) return;
    setIsReady(false);
    map.setStyle(opts.styleSpec);
    map.once('styledata', () => setIsReady(true));
  }, [opts.styleSpec]);

  // Fit bounds on demand
  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const fly = applyViewHint(opts.view ?? null);
    if (fly) {
      map.flyTo({ center: fly.center, zoom: fly.zoom, bearing: fly.bearing });
    }
  }, [opts.view]);

  // PNG export
  const exportPng = useCallback(async (): Promise<Blob> => {
    const map = mapRef.current;
    if (!map) throw new Error('map not yet initialized');
    return captureMapCanvas(map.getCanvas() as HTMLCanvasElement);
  }, []);

  const exportThumbnail = useCallback(
    async (_size?: { w: number; h: number }): Promise<string> => {
      const blob = await exportPng();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    [exportPng],
  );

  return {
    containerRef,
    mapInstance,
    isReady,
    fitBounds,
    exportPng,
    exportThumbnail,
    setStyle,
  };
}
